import api from "./axios";

export interface User {
  id: string;
  email: string;
  isAdmin: boolean;
  numberOfOrganizations?: number;
  createdAt: string;
  organizationIds?: number[];
}

export interface Organization {
  id: number;
  name?: string;
  company?: string;
  department?: string;
  registrationNumber?: string;
  portalDiscountPct?: number;
  priceLevelCode?: string;
}

export interface PasswordChangeRequest {
  userId: string;
  newPassword: string;
}

/**
 * Fetches all users
 * @returns A promise that resolves to an array of users
 */
export const getUsers = async (): Promise<User[]> => {
  try {
    const response = await api.get("/custom/gastrofresh/admin/users");
    return response.data.rows;
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

/**
 * Fetches a specific user by ID
 * @param userId The ID of the user to fetch
 * @returns A promise that resolves to the user data
 */
export const getUserById = async (userId: string): Promise<User> => {
  try {
    const response = await api.get(`/custom/gastrofresh/admin/users/${userId}`);
    return response.data.record;
  } catch (error) {
    console.error(`Error fetching user ${userId}:`, error);
    throw error;
  }
};

/**
 * Fetches all organizations
 * @returns A promise that resolves to an array of organizations
 */
export const getOrganizations = async (): Promise<Organization[]> => {
  try {
    const response = await api.get("/custom/gastrofresh/admin/organizations");
    return response.data.rows;
  } catch (error) {
    console.error("Error fetching organizations:", error);
    throw error;
  }
};

/**
 * Adds an organization to a user
 * @param userId The ID of the user
 * @param organizationId The ID of the organization to add
 * @returns A promise that resolves when the organization is added
 */
export const addOrganizationToUser = async (
  userId: string,
  organizationId: number,
): Promise<void> => {
  try {
    await api.post(`/custom/gastrofresh/admin/users/${userId}/organizations/${organizationId}`);
  } catch (error) {
    console.error(`Error adding organization ${organizationId} to user ${userId}:`, error);
    throw error;
  }
};

/**
 * Removes an organization from a user
 * @param userId The ID of the user
 * @param organizationId The ID of the organization to remove
 * @returns A promise that resolves when the organization is removed
 */
export const removeOrganizationFromUser = async (
  userId: string,
  organizationId: number,
): Promise<void> => {
  try {
    await api.delete(`/custom/gastrofresh/admin/users/${userId}/organizations/${organizationId}`);
  } catch (error) {
    console.error(`Error removing organization ${organizationId} from user ${userId}:`, error);
    throw error;
  }
};

/**
 * Changes a user's password
 * @param userId The ID of the user
 * @param newPassword The new password
 * @returns A promise that resolves when the password is changed
 */
export const changeUserPassword = async (userId: string, newPassword: string): Promise<void> => {
  try {
    await api.put(`/custom/gastrofresh/admin/users/${userId}/password`, { newPassword });
  } catch (error) {
    console.error(`Error changing password for user ${userId}:`, error);
    throw error;
  }
};

/**
 * Changes a user's own password
 * @param oldPassword The current password
 * @param newPassword The new password
 * @returns A promise that resolves when the password is changed
 */
export const changeUserMyPassword = async (
  oldPassword: string,
  newPassword: string,
): Promise<void> => {
  try {
    await api.put(`/custom/gastrofresh/profile/password`, { oldPassword, newPassword });
  } catch (error) {
    console.error(`Error changing my password`, error);
    throw error;
  }
};

export const registerUser = async (
  email: string,
  password: string,
  ico: string,
  phone: string,
  note: string,
): Promise<void> => {
  try {
    await api.post("/custom/gastrofresh/register", {
      Username: email,
      Password: password,
      Ico: ico,
      Phone: phone,
      Note: note,
    });
  } catch (error) {
    console.error("Error registering user:", error);
    throw error;
  }
};

export const sendPasswordResetEmail = async (username: string): Promise<void> => {
  try {
    await api.post("/custom/gastrofresh/forgot-password", { Username: username });
  } catch (error) {
    console.error("Error sending password reset email:", error);
    throw error;
  }
};

export const verifyResetToken = async (userId: string, resetToken: string) => {
  try {
    const response = await api.post("/custom/gastrofresh/verify-reset-token", {
      UserId: userId,
      ResetToken: resetToken,
    });
  } catch (error) {
    console.error("Error verifying reset token:", error);
    throw error;
  }
};

export const resetPassword = async (
  userId: string,
  resetToken: string,
  newPassword: string,
): Promise<void> => {
  try {
    await api.post("/custom/gastrofresh/reset-password", {
      UserId: userId,
      ResetToken: resetToken,
      NewPassword: newPassword,
    });
  } catch (error) {
    console.error("Error resetting password:", error);
    throw error;
  }
};

export const formatOrganizationName = (organization: Organization): string => {
  let output = "";
  if (organization.company && organization.name) {
    output = `${organization.company} (${organization.name})`;
  } else if (organization.company) {
    output = organization.company;
  } else if (organization.name) {
    output = organization.name;
  }
  if (organization.registrationNumber) {
    output += ` - ${organization.registrationNumber}`;
  }
  if (organization.department) {
    output = `${organization.department} - ${output}`;
  }
  return output;
};
